import { z } from 'zod';
import { MoneySchema } from './common.js';
import { JEStatusSchema } from './journal-entry.js';
// Decimal columns may serialize as canonical money strings ("1500.00") OR as
// signed numbers when the API converts on the way out (e.g. netIncome can be
// negative). The union accepts both forms; coerce.number() captures negatives
// that MoneySchema (gte(0)) would reject.
const DecimalLike = z.union([MoneySchema, z.coerce.number()]);
export const DashboardKpiSchema = z.object({
    cash: DecimalLike,
    ar: DecimalLike,
    ap: DecimalLike,
    revenue: DecimalLike, // YTD through end-of-period
    expense: DecimalLike, // YTD COST+EXPENSE through end-of-period (wire-compat)
    cost: DecimalLike, // YTD COST-type only (for true gross margin)
    netIncome: DecimalLike, // revenue − expense YTD
    gpm: z.coerce.number(), // net margin % (legacy field; kept for back-compat)
    // (revenue − cost) / revenue — true gross margin. null when revenue ≤ 0.
    grossMarginPct: z.coerce.number().nullable(),
    // (netIncome + interest + FA-dep + lease-dep) / revenue. Tax add-back omitted
    // (no INCOME_TAX_EXPENSE key). null when revenue ≤ 0.
    ebitdaMarginPct: z.coerce.number().nullable(),
});
// Period-over-period KPI delta. `deltaPct` is null when the comparison base is
// ≤ 0 (no prior data — e.g. a freshly-live company has no YoY/quarter base);
// the UI then renders "—". `dir` is arithmetic; COLOR is decided in the FE via a
// per-KPI good-when-up rule (cash/revenue good-when-up; ar/ap good-when-down).
export const KpiDeltaSchema = z.object({
    deltaPct: z.coerce.number().nullable(),
    dir: z.enum(['up', 'down', 'flat']),
    sub: z.string(),
});
export const DashboardKpiDeltasSchema = z.object({
    cash: KpiDeltaSchema, // vs end of previous quarter
    ar: KpiDeltaSchema, // vs end of previous month (MoM)
    ap: KpiDeltaSchema, // vs end of previous month (MoM)
    revenue: KpiDeltaSchema, // YTD vs same window last year (YoY)
});
// A real, actionable queue item. Only emitted when its count > 0.
export const DashboardTaskSchema = z.object({
    key: z.enum(['draft-journals', 'bank-recon', 'period-close']),
    count: z.number().int(),
    title: z.string(),
    sub: z.string(),
    route: z.string(),
    tone: z.enum(['negative', 'warning', 'info', 'muted']),
});
export const DashboardJournalEntrySchema = z.object({
    id: z.uuid(),
    voucherNo: z.string(),
    voucherDate: z.coerce.date(),
    description: z.string(),
    status: JEStatusSchema,
    totalDebit: DecimalLike,
});
export const DashboardArAgingSchema = z.object({
    notDue: DecimalLike,
    d1_30: DecimalLike,
    d31_60: DecimalLike,
    d61_90: DecimalLike,
    over90: DecimalLike,
});
export const DashboardResponseSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
    kpi: DashboardKpiSchema,
    // Rolling 12-month series ending at `period` (millions of baht, 1 dp).
    revenueByMonth: z.array(z.coerce.number()).length(12),
    expenseByMonth: z.array(z.coerce.number()).length(12),
    // Up to 8 most recent POSTED journals for the "Recent activity" panel.
    recentJournals: z.array(DashboardJournalEntrySchema),
    // AR subledger is Phase 4 — stub returns all zeros for now.
    arAging: DashboardArAgingSchema,
    // Tier-1 real data (GL-coherent): period-over-period KPI deltas + task queue.
    kpiDeltas: DashboardKpiDeltasSchema,
    tasks: z.array(DashboardTaskSchema),
});
export const DashboardQuerySchema = z.object({
    period: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
});
//# sourceMappingURL=dashboard.js.map